import { Box, Divider, Typography, useTheme } from "@mui/material"
import { styled } from "@mui/material/styles"
import PaperBox from "components/PaperBox"
import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { ExamAreaHeader } from "./ExamAreaHeader/ExamAreaHeader"
import { MultiQuestion } from "./MultiQuestion"
import { SingleQuestion } from "./SingleQuestion"

const StyledPaperBox = styled(PaperBox)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginBottom: 50
  }
}))

export const Area = ({
  area,
  activeStep,
  examinationType,
  translatedTitle,
  vimeoUrl,
  setFilteredFormAnswers
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const courseDetails = t(`learning.details.course`, { returnObjects: true }).find(
    (course) => course.examinationType === examinationType
  ).examinationDefinition

  const getQuestionByShuffleId = (q) => {
    let questionByShuffleId = courseDetails.areas[activeStep].areaQuestions.flatMap((aQ) =>
      aQ.questions.filter((x) => x.shuffleId === q.shuffleId)
    )[0]

    if (questionByShuffleId === undefined) {
      questionByShuffleId = courseDetails.areas[activeStep].areaQuestions.flatMap((aQ) =>
        aQ.questions.filter((x) => x.id === q.id)
      )[0]
    }

    return questionByShuffleId
  }

  useEffect(() => {
    if (area?.areaQuestions?.length > 0 && area.title) {
      const newAreaQuestions = area.areaQuestions.map((q) => {
        const questionByShuffleId = getQuestionByShuffleId(q)

        return {
          id: questionByShuffleId.id,
          isMultiChoice: questionByShuffleId.isMultiChoice === "true",
          options: questionByShuffleId.options.map((option) => ({
            ...option,
            isCorrectAnswer: option.isCorrectAnswer === "true"
          })),
          question: questionByShuffleId.title,
          shuffleId: questionByShuffleId.shuffleId
        }
      })

      setFilteredFormAnswers((prev) => {
        const existingAreaIndex = prev.findIndex((item) => item.title === area.title)
        if (existingAreaIndex !== -1) {
          const updatedAreas = [...prev]
          updatedAreas[existingAreaIndex] = {
            ...updatedAreas[existingAreaIndex],
            areaQuestions: newAreaQuestions
          }
          return updatedAreas
        } else {
          return [
            ...prev,
            {
              text: area.text,
              information: area.information,
              html: area.html,
              title: area.title,
              areaQuestions: newAreaQuestions,
              questions: newAreaQuestions
            }
          ]
        }
      })
    }
  }, [area, activeStep])

  return (
    <div>
      <ExamAreaHeader translatedTitle={translatedTitle} vimeoUrl={vimeoUrl} data={area} />
      {area?.areaQuestions?.length > 0 && (
        <StyledPaperBox>
          <Typography variant="h3">{t("global.titles.questions", "Questions")}</Typography>
          {area?.areaQuestions?.map((q, key) => {
            const questionByShuffleId = getQuestionByShuffleId(q)

            return (
              <Box marginY={2} key={area.title + "_" + key}>
                <Typography variant="h6">{questionByShuffleId?.title}</Typography>
                {q.isMultiChoice && (
                  <Typography variant="bodySmall" color="textSecondary" marginBottom={2}>
                    {t(
                      "learning.exams.titles.severalOptions",
                      "ATTENTION! Several options can be specified and be correct"
                    )}
                  </Typography>
                )}

                {q.isMultiChoice ? (
                  <MultiQuestion question={q} />
                ) : (
                  <SingleQuestion
                    translationOptions={questionByShuffleId?.options}
                    area={area.areaQuestions}
                    question={q}
                  />
                )}
                {key !== area?.areaQuestions?.length - 1 && <Divider light sx={{ marginTop: 2 }} />}
              </Box>
            )
          })}
        </StyledPaperBox>
      )}
    </div>
  )
}
